import { useCallback, useEffect, useState } from 'react';
import { normalizeSearchPhrase } from '../../../../helpers/normalizeSearchPhrase';
import { useLookerSuggestions } from '../../../../hooks/useLooker';
import { useLookerDashboardContext } from '../../../../providers/LookerDashboardProvider';

interface UseFilterSuggestionsOptions {
  /** Hard on/off. When `false` the query never fires. Default `true`. */
  enabled?: boolean;
  /**
   * Defer the suggestions fetch until the consumer signals it needs them
   * (either by calling the returned `activate()` or by typing into the
   * search input). Use this in filter popovers so we only hit
   * `/v7/looker/suggestions` when the dropdown actually opens — mirrors
   * the on-demand behaviour of the old Looker extension filter component.
   */
  lazy?: boolean;
}

const useFilterSuggestions = (
  filterName: string,
  { enabled = true, lazy = false }: UseFilterSuggestionsOptions = {},
) => {
  const { dashboardFilters } = useLookerDashboardContext();

  const filterConfig = dashboardFilters?.find((filter) => filter.name === filterName);
  const defaultFilterValue = filterConfig?.field?.default_filter_value || '';

  const [rawSuggestions, setRawSuggestions] = useState<string[]>([]);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isActivated, setIsActivated] = useState(!lazy);

  const queryEnabled = enabled && !!filterConfig && (!lazy || isActivated);

  const {
    data,
    isLoading: queryLoading,
    isError: queryIsError,
  } = useLookerSuggestions(
    {
      lookml_model_name: filterConfig?.model,
      explore_name: filterConfig?.explore,
      dimension_name: filterConfig?.field?.name ?? undefined,
      term: searchTerm || undefined,
    },
    { enabled: queryEnabled },
  );

  const suggestions = data?.data?.result?.suggestions;
  const isLoading = queryEnabled && queryLoading;

  useEffect(() => {
    if (enabled && !filterConfig) {
      setError(`Filter configuration for ${filterName} not found`);
      return;
    }
    if (!queryEnabled || queryLoading) {
      return;
    }
    if (queryIsError || !suggestions) {
      setError('No suggestions available');
      return;
    }
    setRawSuggestions(suggestions);
    setFilteredSuggestions(suggestions);
    setError(null);
  }, [enabled, queryEnabled, filterConfig, queryLoading, queryIsError, suggestions, filterName]);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredSuggestions(rawSuggestions);
    } else {
      const lowerSearch = searchTerm.toLowerCase();
      const filtered = rawSuggestions.filter((item) => item.toLowerCase().includes(lowerSearch));
      setFilteredSuggestions(filtered);
    }
  }, [searchTerm, rawSuggestions]);

  const onSearchPhraseChange = useCallback((value: string) => {
    const normalizedValue = normalizeSearchPhrase(value);
    setSearchTerm(normalizedValue);
    setIsActivated(true);
  }, []);

  const activate = useCallback(() => {
    setIsActivated(true);
  }, []);

  return {
    suggestions: filteredSuggestions,
    isLoading,
    error,
    onSearchPhraseChange,
    defaultFilterValue,
    searchTerm,
    /** Call from a popover's `onOpen` (or equivalent) when `lazy: true`. No-op otherwise. */
    activate,
  };
};

export default useFilterSuggestions;
